import { useMemo } from 'react';

import { getFoldersLOV } from 'api/folders/operations/getFoldersLOV';
import { folderPathsById } from 'helpers/folderPaths';

import { EditReference } from './EditReference';
import type { ReferenceSource } from './referenceSource';

// The minimal identifying shape this control deals in. The wire saves only
// `folder_id`; `path` is the resolved root→leaf display string ("Parent /
// Child"). `path` is carried so a value loaded from the server (which arrives
// as `folder_id` + `folder_path`) renders its label before the option list
// loads — and selection matches on `folder_id` regardless.
export interface FolderReference {
  readonly folder_id: number;
  readonly path: string;
}

interface Props {
  readonly value: FolderReference | null;
  readonly onChange: (value: FolderReference | null) => void;
  // Restrict options to one zone's folders. Folders are zone-scoped, so the
  // picker mirrors the entity's selected zone; clears to all when undefined.
  readonly zoneName?: string | null;
  readonly label?: string;
  readonly mandatory?: boolean;
  readonly disabled?: boolean;
}

const getKey = (f: FolderReference) => f.folder_id;
const getLabel = (f: FolderReference) => f.path;

/**
 * Searchable dropdown for folders, for the new edit-model infrastructure
 * (recurrence_definitions). Mirrors {@link EditSchedulingWindowReference}, but
 * eager-loaded: there's no `/folders` search endpoint and the folder universe
 * is small, so it loads the flat list once and filters client-side. Options are
 * scoped to `zoneName` and rendered as their full path.
 */
export const EditFolderReference = ({ value, onChange, zoneName, label = 'Folder', mandatory, disabled }: Props) => {
  // Rebuild the loader when the zone changes so the options re-scope. Same
  // (zoneName) yields the same function reference, so the model isn't churned.
  const source = useMemo<ReferenceSource<FolderReference>>(
    () => ({
      kind: 'eager',
      load: async () => {
        // Folders are zone-scoped: with no zone selected there are no valid
        // folders to offer.
        if (!zoneName) return [];
        const folders = await getFoldersLOV();
        const scoped = folders.filter((f) => f.zone_name === zoneName);
        const paths = folderPathsById(scoped);
        return scoped
          .map((f) => ({
            folder_id: f.folder_id,
            path: paths.get(f.folder_id) ?? f.name,
          }))
          .sort((a, b) => a.path.localeCompare(b.path));
      },
    }),
    [zoneName]
  );

  return (
    <EditReference<FolderReference>
      label={label}
      value={value}
      onChange={onChange}
      source={source}
      getKey={getKey}
      getLabel={getLabel}
      mandatory={mandatory}
      disabled={disabled}
    />
  );
};
